/**
 * The app's own texts (Resources/Xml/<lang>.xml, LanguageManager.Get) and the
 * image / sound library index (LibrariesImages.xml / LibrariesSounds.xml),
 * staged as JSON by tools/src/stage-libraries.mjs:
 *   /libraries/xml/<lang>.json   { UI: {id: text}, ImageLibraries: {…}, SoundLibraries, SoundsItems }
 *   /libraries/libraries.json    { images: Library[], sounds: Library[] }
 * The editor loads them once before it renders.
 */
import { currentLanguage } from "../player/i18n";

type Texts = Record<string, Record<string, string>>;

const XML_LANGS = ["ar", "ca", "de", "en", "es", "fr", "it", "pt", "eu", "gl", "nl", "pl", "ro", "sv", "el", "tr", "ru", "uk", "id", "vi", "cs", "sk", "hu", "hr", "bg", "da", "nb", "fi", "fa", "ur", "ms", "az", "uz", "fil"];
let texts: Texts = {};
let fallback: Texts = {};
let loaded: Promise<void> | null = null;

async function fetchTexts(lang: string): Promise<Texts> {
  try {
    const r = await fetch(`/libraries/xml/${lang}.json`);
    return r.ok ? ((await r.json()) as Texts) : {};
  } catch {
    return {};
  }
}

/** Loads the texts of the UI language (and English as fallback). */
export function loadAppTexts(): Promise<void> {
  if (!loaded) {
    const lang = XML_LANGS.includes(currentLanguage()) ? currentLanguage() : "en";
    loaded = Promise.all([fetchTexts(lang), lang === "en" ? Promise.resolve(null) : fetchTexts("en")]).then(([a, en]) => {
      texts = a;
      fallback = en ?? a;
    });
  }
  return loaded;
}

/** Texts some languages' XML lack (they would show in English). */
const MISSING: Record<string, Record<string, string>> = {
  ar: { slideNameSequences: "تسلسل" },
  ca: { slideNameSequences: "Seqüència" },
};

/** LanguageManager.Get(section, id). */
export function ut(id: string, section = "UI"): string {
  return texts[section]?.[id] ?? (section === "UI" ? MISSING[currentLanguage()]?.[id] : undefined) ?? fallback[section]?.[id] ?? id;
}

/** ut, with a default for texts some languages' XML lack. */
export function utOr(id: string, otherwise: string): string {
  return texts.UI?.[id] ?? MISSING[currentLanguage()]?.[id] ?? fallback.UI?.[id] ?? otherwise;
}

export interface ImageLibrary {
  id: string;
  icon: string;
  subs: { icon: string; items: string[] }[];
}
export interface SoundLibrary {
  id: string;
  icon: string;
  subs: { icon: string; items: { path: string; text: string }[] }[];
}

let libraries: Promise<{ images: ImageLibrary[]; sounds: SoundLibrary[] }> | null = null;

export function loadLibraries(): Promise<{ images: ImageLibrary[]; sounds: SoundLibrary[] }> {
  if (!libraries) {
    libraries = fetch("/libraries/libraries.json")
      .then((r) => (r.ok ? r.json() : { images: [], sounds: [] }))
      .catch(() => ({ images: [], sounds: [] }));
  }
  return libraries;
}
